import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';

export default function Godsmaengder() {
  return (
    <>
      <SEO
        title="Godsmængde og Sendingsstørrelser | Fastlane ApS"
        description="Godsmængdens betydning for transportens planlægning og gennemførelse. Læs om pakker, stykgods, partilæs og hele læs (full loads)."
        canonicalUrl="https://fastlane.dk/godsmaengder-sendingsstoerrelser"
      />

      <PageHeader
        title="Godsmængder og Sendingsstørrelser"
        subtitle="Forstå forskellen mellem pakker, stykgods, partigods og full loads, og få den mest optimale transportform."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Godsmængder og Sendingsstørrelser' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
            Godsmængden eller sendingesstørrelsen er af stor betydning for, hvordan en fragt planlægges og genneføres.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Pakker */}
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200/90 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-800 flex items-center justify-center font-bold">
                <i className="fa-solid fa-box" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-[#0f2b48]">Pakker</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Ofte sendes de mindste forsendelser i transportsystemer målrettet mod netop håndtering af pakker. Kurérforsendeler er eksempler på det.
              </p>
            </div>

            {/* Stykgods */}
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200/90 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-800 flex items-center justify-center font-bold">
                <i className="fa-solid fa-boxes-stacked" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-[#0f2b48]">Stykgods</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Småforsendelser som ikke med fordel kan sendes som pakker grundet vægt, dimension eller forhold omkring afhentning eller levering, farlig gods (ADR-gods) sendes som stykgods. 1-2 paller og op til ca. 1500 kg sendes normalt som stykgods. Stykgods hentes og leveres typisk med indenrigsdistribution i afsender- og leveringslandet.
              </p>
              <p className="text-xs text-slate-500 font-medium">
                Transittiden er typisk lidt længere fordi der anvendes lokal distribution i begge ender af transporten.
              </p>
            </div>

            {/* Partlæs / Partigods */}
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200/90 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-800 flex items-center justify-center font-bold">
                <i className="fa-solid fa-truck-ramp-box" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-[#0f2b48]">Partlæs / Partigods</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Større forsendelser fra 3 paller og op efter sendes som delpartier, som hentes direkte med eksport / import trailer. Den betyder, at samme trailer typisk henter ved forskellige afsendere og køre uden omlæsning til forskellige modtagere.
              </p>
              <p className="text-xs text-slate-500 font-medium">
                Transittiden er typisk af medium længde fordi der konsolideres (samlæsses med andet gods).
              </p>
            </div>

            {/* Hele læs / Full loads */}
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200/90 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-800 flex items-center justify-center font-bold">
                <i className="fa-solid fa-truck" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-bold text-[#0f2b48]">Hele læs / Full loads</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                En trailer henter et helt læs og køre direkte til modtager uden omlæsning og uden konsolidering med andet gods. Transittiden er typisk kort fordi godset køres fra afsender og direkte til modtager.
              </p>
              <p className="text-xs text-slate-500 font-medium">
                Samlastningsskader og håndteringsskader kan så vidt muligt undgås fordi der kun er en afsender og en modtager.
              </p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-sky-50 text-slate-700 text-xs sm:text-sm border border-sky-100 italic">
            Overgangen mellem de forskellige sendingsstørrelser er flydende og ovenstående er kun vejledende.
          </div>

          <div className="pt-4 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center sm:justify-between border-t border-slate-200">
            <span className="text-sm text-slate-600">Har du brug for rådgivning om din specifikke sendingsstørrelse?</span>
            <Link
              to="/fragtrater"
              className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-[#b25d19] text-white text-sm font-semibold hover:bg-[#974d12] text-center"
            >
              Få et uforpligtende tilbud
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
